import { DatePipe } from '@angular/common';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@rtls-environment/environment';

import { PlanningEntry } from '@rtls/core/models/planning/planning-entry.model';
import { PlanningStatistic } from '@rtls/core/models/planning/planning-statistic.model';
import { FileExtension } from '@rtls/core/models/rpc/file-extension.model';
import { FileFormatHeader } from '@rtls/core/models/rpc/file-format-header';
import { SearchOperation } from '@rtls/core/models/rpc/search-operation';
import { SearchQuery } from '@rtls/core/models/rpc/search-query';
import { RtlsModels } from '@rtls/core/models/rtls/rtls-models';
import { RtlsServiceDecorator } from '@rtls/core/models/rtls/rtls-service.decorator';
import { RtlsService } from '@rtls/core/models/rtls/rtls-service.model';
import { Observable } from 'rxjs';

@Injectable()
export class PlanningService extends RtlsServiceDecorator<PlanningEntry> {
	constructor(
		private http: HttpClient,
		rtlsService: RtlsService<PlanningEntry>,
		private datesPipe: DatePipe,
	) {
		super(rtlsService.useModel(RtlsModels.PlanningEntry));
	}

	generateFileNameBasedOnDate(date: Date, fileExtension: string) {
		return `planning_${this.datesPipe.transform(date, 'dd-MM_yyyy')}.${fileExtension}`;
	}

	getPlanningStatistic(startDate: Date, endDate: Date): Observable<PlanningStatistic[]> {
		return this.http.get<PlanningStatistic[]>(`${environment.thingsApi}/${RtlsModels.PlanningEntry}/statistics`, {
			params: this.getParamsFromPlanningStatistic(startDate, endDate),
		});
	}

	private getParamsFromPlanningStatistic(startDate: Date, endDate: Date): HttpParams {
		return new HttpParams().set('startDate', startDate.toUTCString()).set('endDate', endDate.toUTCString());
	}

	addComment(planningEntryId: number, commentText: string): Observable<PlanningEntry> {
		const content = { content: commentText };
		return this.http.post<PlanningEntry>(
			`${environment.thingsApi}/${RtlsModels.PlanningEntry}/${planningEntryId}/comment`,
			content,
		);
	}

	completePlanning(planningEntry: PlanningEntry): Observable<PlanningEntry> {
		return this.http.post<PlanningEntry>(
			`${environment.thingsApi}/${RtlsModels.PlanningEntry}/${planningEntry.id}/complete`,
			planningEntry,
		);
	}

	cancelPlanning(planningEntry: PlanningEntry): Observable<PlanningEntry> {
		return this.http.post<PlanningEntry>(
			`${environment.thingsApi}/${RtlsModels.PlanningEntry}/${planningEntry.id}/cancel`,
			planningEntry,
		);
	}

	getFile(fileType: FileExtension, dates: [Date, Date], endpoint: string): [Observable<any>, string] {
		const query = SearchQuery.AND(
			new SearchQuery('arrivalDate', SearchOperation.STARTS_WITH, dates[0]),
			new SearchQuery('arrivalDate', SearchOperation.ENDS_WITH, dates[1]),
		);

		const headers = new HttpHeaders({
			Accept: FileFormatHeader.getCorrespondingAcceptHeaderValue(fileType),
		});

		const fileName = this.generateFileNameBasedOnDate(dates[0], fileType);
		const postQuery = this.http.post(`${environment.thingsApi}/${RtlsModels.PlanningEntry}/${endpoint}`, query, {
			headers: headers,
			responseType: 'blob',
			params: { timeZone: 'Europe/Paris' },
		});
		return [postQuery, fileName];
	}

	getFileQuery(fileType: FileExtension, query: SearchQuery, endpoint: string): [Observable<any>, string] {
		const headers = new HttpHeaders({
			Accept: FileFormatHeader.getCorrespondingAcceptHeaderValue(fileType),
		});

		const fileName = this.generateFileNameBasedOnDate(new Date(), fileType);
		const postQuery = this.http.post(`${environment.thingsApi}/${RtlsModels.PlanningEntry}/${endpoint}`, query, {
			headers: headers,
			responseType: 'blob',
			params: { timeZone: 'Europe/Paris' },
		});
		return [postQuery, fileName];
	}
}
